<template>
  <v-container fluid>
    <div class="ts-plans-container">
      <h1 class="subs-title">{{ adminText('organizationAll.list_of_subscription_plans_5ec418b5', 'List of subscription plans') }}</h1>
      <p class="page-description">{{ adminText('organizationAll.add_and_manage_your_subscriptions_4b10a220', 'Add and manage your subscriptions.') }}</p>
      <div class="plans-toolbar">
        <div class="plan-filters">
          <v-select
            v-model="seasonFilter"
            :items="organizationSentinelOptions(seasonOptions, 'text')"
            item-text="text"
            item-value="value"
            :label="adminText('organizationAll.season_62255f56', 'Season')"
            outline
            hide-details
          />
          <v-select
            v-model="statusFilter"
            :items="organizationOptions(statusOptions, 'text')"
            item-text="text"
            item-value="value"
            :label="adminText('organizationAll.status_bae7d5be', 'Status')"
            outline
            hide-details
          />
        </div>
        <div class="plan-actions">
          <main-button outline @click="previewSubscriptionsPurchasePage">
            <v-icon small class="mr-2">visibility</v-icon>{{ adminText('organizationAll.preview_subscriptions_386878f3', 'Preview subscriptions') }}
          </main-button>
          <main-button depressed @click="navigateToCreateSubscription">
            <v-icon small class="mr-2">add</v-icon>{{ adminText('organizationAll.create_new_subscription_ad2d4acf', 'Create new subscription') }}
          </main-button>
        </div>
      </div>
      <div v-if="$vuetify.breakpoint.smAndDown" class="plan-mobile-sort">
        <v-select
          v-model="planPagination.sortBy"
          :items="capacityListHeaders.filter(header => header.sortable !== false)"
          item-text="text"
          item-value="value"
          :label="adminText('organizationAll.plan_sort_by', 'Sort by')"
          hide-details
          @change="planPagination.page = 1"
        />
        <v-btn icon :aria-label="adminText('organizationAll.plan_reverse_sort', 'Reverse sort order')" :aria-pressed="String(planPagination.descending)" @click="planPagination.descending = !planPagination.descending">
          <v-icon>{{ planPagination.descending ? 'arrow_downward' : 'arrow_upward' }}</v-icon>
        </v-btn>
      </div>
      <v-progress-linear v-if="loading && $vuetify.breakpoint.smAndDown" indeterminate height="2" />
      <v-card class="card">
        <v-card-text>
          <v-data-table
            :key="filteredSubscriptionPlans.length"
            :headers="capacityListHeaders"
            :hide-headers="$vuetify.breakpoint.smAndDown"
            :pagination.sync="planPagination"
            :items="filteredSubscriptionPlans"
            :loading="loading"
            :no-data-text="organizationText(noDataText)"
            class="compact-table"
            :rows-per-page-items="[10, 25, 50]"
          >
            <template v-slot:items="props">
              <tr class="clickable-row" @click="editSubscriptionPlan(props.item)">
                <td class="plan-name"><button type="button" class="plan-name-button" @click.stop="editSubscriptionPlan(props.item)">{{ props.item.name }}</button></td>
                <td :data-label="capacityListHeaders[1].text" @click.stop>
                  <template v-if="props.item.generalAdmissionCapacity">
                    <v-btn
                      flat
                      small
                      class="capacity-expand"
                      :aria-label="adminText('organizationAll.ga_capacity', 'Subscription capacity') + ' — ' + props.item.name"
                      :aria-expanded="String(props.expanded)"
                      @click="props.expanded = !props.expanded"
                    >
                      {{ props.item.generalAdmissionCapacity.capacityLimit == null ? '—' : props.item.generalAdmissionCapacity.capacityLimit }}
                      <v-icon small aria-hidden="true">{{ props.expanded ? 'expand_less' : 'expand_more' }}</v-icon>
                    </v-btn>
                  </template><span v-else>—</span>
                </td>
                <td :data-label="capacityListHeaders[2].text">{{ props.item.price }} €</td>
                <td :data-label="capacityListHeaders[3].text">{{ props.item.venue ? props.item.venue.name : '' }}</td>
                <td :data-label="capacityListHeaders[4].text">{{ formatDate(props.item.valid_from) }}</td>
                <td :data-label="capacityListHeaders[5].text">{{ formatDate(props.item.valid_to) }}</td>
                <td :data-label="capacityListHeaders[6].text">
                  {{ props.item.activeSubscriberCount }}
                </td>
                <td :data-label="capacityListHeaders[7].text">
                  <v-chip small :color="props.item.active ? 'green' : 'red'" text-color="white">
                    {{ props.item.active ? adminText('organizationAll.active_a733b809', 'Active') : adminText('organizationAll.inactive_09af574c', 'Inactive') }}
                  </v-chip>
                </td>
                <td class="plan-row-actions" :data-label="capacityListHeaders[8].text">
                  <v-btn icon :aria-label="adminText('organizationAll.edit_5301648d', 'Edit') + ' — ' + props.item.name" @click.stop="editSubscriptionPlan(props.item)">
                    <v-icon small>edit</v-icon>
                  </v-btn>
                  <v-btn icon :aria-label="adminText('organizationAll.delete_f6fdbe48', 'Delete') + ' — ' + props.item.name" @click.stop="deleteSubscriptionPlan(props.item)">
                    <v-icon small>delete</v-icon>
                  </v-btn>
                </td>
              </tr>
            </template>
            <template v-slot:expand="props">
              <theater-subscription-capacity class="plan-list-capacity" :summary="props.item.generalAdmissionCapacity" />
            </template>
            <template v-slot:no-data>
              <div class="text-center">
                {{
                  subscriptionPlans.length
                    ? adminText('organizationAll.no_subscription_plans_match_the_selected_filt_392a941d', 'No subscription plans match the selected filters')
                    : adminText('organizationAll.no_subscription_plans_available_9f56a637', 'No subscription plans available')
                }}
              </div>
            </template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </div>

    <!-- Edit Subscription Plan Modal -->
    <v-dialog
      v-model="showSubscriptionPlanModal"
      max-width="1200px"
      width="calc(100vw - 32px)"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      scrollable
      content-class="modal subscription-plan-dialog"
    >
      <v-card class="subscription-plan-edit-modal">
        <v-card-title>
          <span class="headline">{{ adminText('organizationAll.edit_theater_subscription_plan_e830157e', 'Edit Theater Subscription Plan') }}</span>
          <v-btn icon class="plan-edit-close" :aria-label="adminText('organizationAll.close_bbfa773e', 'Close')" @click="showSubscriptionPlanModal = false">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pa-6">
          <v-form ref="form" v-model="valid" lazy-validation class="subscription-plan-form" :class="{ 'subscription-plan-form--with-capacity': capacityIsGeneralAdmission }">
            <div class="plan-edit-details">
              <v-flex xs12 sm12 class="plan-edit-fields d-flex justify-space-between align-center">
                <v-text-field
                  v-model="subscriptionPlan.name"
                  style="flex: 1 !important"
                  :label="adminText('organizationAll.name_709a2322', 'Name')"
                  required
                  :rules="[v => !!v || adminText('organizationAll.name_is_required_222c72b1', 'Name is required')]"
                />
                <v-switch
                  v-model="subscriptionPlan.active"
                  :label="adminText('organizationAll.active_a733b809', 'Active')"
                  style="flex: 1 !important; display: grid; place-items: center"
                />
              </v-flex>
              <v-flex xs12 sm12 class="plan-edit-fields d-flex justify-space-between align-center" style="gap: 1rem">
                <v-select
                  v-model="selectedVenue"
                  :items="venues"
                  :label="adminText('organizationAll.venue_67cd4061', 'Venue')"
                  item-text="displayName"
                  item-value="id"
                  :value-comparator="(a, b) => Number(a) === Number(b)"
                  :rules="[v => !!v || adminText('organizationAll.venue_is_required_2be23251', 'Venue is required')]"
                  required
                  :disabled="isSubscriptionDataLocked"
                  :error-messages="organizationMessages(venueErrorMessage ? [venueErrorMessage] : [])"
                  :hint="
                    isSubscriptionDataLocked
                      ? adminText('organizationAll.venue_cannot_be_changed_after_purchases_exist_f95b5817', 'Venue cannot be changed after purchases exist for this plan.')
                      : ''
                  "
                  :append-outer-icon="isSubscriptionDataLocked ? 'lock' : undefined"
                  persistent-hint
                  style="flex: 1 !important"
                />

                <v-text-field
                  v-model="subscriptionPlan.price"
                  :label="adminText('organizationAll.price_eur_6e2ef147', 'Price (EUR)')"
                  style="flex: 1 !important"
                  type="number"
                  required
                  :disabled="isSubscriptionDataLocked"
                  :hint="
                    isSubscriptionDataLocked
                      ? adminText('organizationAll.price_cannot_be_changed_after_purchases_exist_8bf96413', 'Price cannot be changed after purchases exist for this plan.')
                      : ''
                  "
                  :append-outer-icon="isSubscriptionDataLocked ? 'lock' : undefined"
                  persistent-hint
                  :rules="[
                    v => !!v || adminText('organizationAll.price_is_required_2fa9daf0', 'Price is required'),
                    v => v > 0 || adminText('organizationAll.price_must_be_greater_than_0_f0f9a3b5', 'Price must be greater than 0'),
                  ]"
                />
              </v-flex>
              <v-textarea v-model="subscriptionPlan.description" :label="adminText('organizationAll.description_55f8ebc8', 'Description')" rows="4" />

              <!-- Plan cover image moved up under title/date -->
              <div class="mt-4">
                <div class="subtitle-1 mb-2">{{ adminText('organizationAll.plan_cover_image_65791027', 'Plan cover image') }}</div>
                <cover-field
                  v-model="planCoverUrl"
                  preserve-crop
                  :upload-options="{ path: 'theater-subscription-covers', serverProcess: true }"
                />
              </div>
              <v-layout row wrap>
                <v-flex xs12 sm6 pr-2>
                  <date-field v-model="subscriptionPlan.valid_from" :label="adminText('organizationAll.start_date_2fde03a5', 'Start date*')" />
                </v-flex>
                <v-flex xs12 sm6 pl-2>
                  <date-field v-model="subscriptionPlan.valid_to" :label="adminText('organizationAll.end_date_dc7fcbdc', 'End date*')" />
                </v-flex>
              </v-layout>

              <!-- Sales window scheduling -->
              <div class="d-flex justify-space-between align-center mb-3">
                <div class="subtitle-1">{{ adminText('organizationAll.schedule_sales_availability_bef41bf9', 'Schedule sales availability') }}</div>
                <v-switch v-model="subscriptionPlan.schedule_sales_availability" />
              </div>
              <div v-if="subscriptionPlan.schedule_sales_availability">
                <div class="d-flex flex-wrap">
                  <div class="mr-2 flex-grow-1">
                    <date-field
                      v-model="subscriptionPlan.available_from"
                      :label="adminText('organizationAll.from_4a2da276', 'From*')"
                      :max="subscriptionPlan.valid_to"
                      :min="new Date().toString()"
                    />
                  </div>
                  <div class="ml-2 flex-grow-1">
                    <date-field
                      v-model="subscriptionPlan.available_to"
                      :label="adminText('organizationAll.to_c2fdddd0', 'To*')"
                      :max="subscriptionPlan.valid_to"
                      :min="new Date().toISOString()"
                    />
                  </div>
                </div>

                <div class="d-flex align-center justify-start mt-3">
                  <v-checkbox
                    v-model="subscriptionPlan.enable_pre_order"
                    :label="adminText('organizationAll.enable_pre_order_for_existing_subscribers_a7317cc2', 'Enable pre-order for existing subscribers')"
                    class="mt-0 mr-2 justify-start"
                    hide-details
                  />
                  <v-text-field
                    v-if="subscriptionPlan.enable_pre_order"
                    v-model="subscriptionPlan.pre_order_days"
                    type="number"
                    class="ml-2 mr-2 p-0"
                    style="max-width: 80px"
                    hide-details
                  />
                  <span v-if="subscriptionPlan.enable_pre_order">{{ adminText('organizationAll.days_in_advance_89b29b50', 'days in advance') }}</span>
                </div>
              </div>
            </div>
            <theater-subscription-capacity
              v-if="capacityIsGeneralAdmission"
              class="plan-capacity-panel"
              :summary="capacitySummary"
              :loading="capacityLoading"
              :error="capacityError"
              @retry="refreshCapacityPreview"
            />
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <main-button flat color="#333" :text="adminText('organizationAll.cancel_77dfd213', 'Cancel')" @click="showSubscriptionPlanModal = false" />
          <main-button :disabled="!valid || formLoading" :loading="formLoading" :text="adminText('organizationAll.save_efc007a3', 'Save')" @click="saveSubscriptionPlan" />
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="showDeleteDialog" max-width="400">
      <v-card>
        <v-card-title class="headline">{{ adminText('organizationAll.delete_subscription_plan_2459a5c4', 'Delete Subscription Plan') }}</v-card-title>
        <v-card-text> {{ adminText('organizationAll.are_you_sure_you_want_to_delete_this_subscrip_35bdbe50', 'Are you sure you want to delete this subscription plan?') }} </v-card-text>
        <v-card-actions>
          <v-spacer />
          <main-button flat color="#333" :text="adminText('organizationAll.cancel_77dfd213', 'Cancel')" @click="showDeleteDialog = false" />
          <main-button color="red" :text="adminText('organizationAll.delete_f6fdbe48', 'Delete')" @click="confirmDelete" />
        </v-card-actions>
      </v-card>
    </v-dialog>
    <empty-subscription-warning v-model="showEmptyCapacityWarning" :active="subscriptionPlan.active" @confirm="saveSubscriptionPlan(true)" />
    <simple-snackbar v-bind.sync="snackbar" :message-prop="organizationText(snackbar &amp;&amp; (snackbar.messageProp || snackbar.message))" />
  </v-container>
</template>

<script>
import CapacityPreview from './subscription-capacity-preview'
import TheaterSubscriptionCapacity from './TheaterSubscriptionCapacity.vue'
import EmptySubscriptionWarning from './EmptySubscriptionWarning.vue'
import OrganizationLocalization from '@/components/organizations/organization-all-display'

import {
  getTheaterSubscriptions,
  getTheaterSubscription,
} from '@/graphql/theater-subscriptions.graphql.js'
import {
  updateTheaterSubscription,
  deleteTheaterSubscription,
} from '@/graphql/theater-subscriptions.graphql.js'
import { theaterOrganizationRouteNames } from '@/routing/routeNames/theaterOrganizerRouterNames'
import SimpleSnackbar from '@/components/shared/snackbars/simple-snackbar.vue'
import MainButton from '@/components/shared/main-button.vue'
import gql from 'graphql-tag'
import DateField from '@/components/shared/inputs/date-field.vue'
import { sharedRouteNames } from '../../../routing/routeNames/sharedRouteNames'

export default {
  name: 'theater-subscriptions-plan-container',
  components: {
    TheaterSubscriptionCapacity,
    EmptySubscriptionWarning,
    SimpleSnackbar,
    DateField,
    MainButton,
  },
  mixins: [OrganizationLocalization, CapacityPreview],
  props: {
    organizationSlug: {
      type: String,
      required: true,
    },
  },
  data() {
    return {
      planPagination: { sortBy: 'name', descending: false, page: 1, rowsPerPage: 10 },
      formLoading: false,
      loading: false,
      valid: true,
      showSubscriptionPlanModal: false,
      showDeleteDialog: false,
      isEditing: false,
      validFromMenu: false,
      validToMenu: false,
      availableFromMenu: false,
      availableToMenu: false,
      selectedVenue: null,
      venues: [],
      subscriptionPlanToDelete: null,
      planCoverUrl: null,
      purchaseCount: 0,
      purchaseCountLoading: false,
      hasPurchaseHistory: false,
      originalPlanPrice: null,
      venueErrorMessage: '',
      subscriptionPlan: {
        id: null,
        name: '',
        price: '',
        valid_from: '',
        valid_to: '',
        active: true,
        event_template_id: null,
        schedule_sales_availability: false,
        available_from: '',
        available_to: '',
        enable_pre_order: false,
        pre_order_days: 14,
        description: '',
        venueId: null,
      },
      eventTemplates: [],
      subscriptionPlans: [],
      search: '',
      seasonFilter: null,
      statusFilter: null,

      headers: [
        { text: 'Name', value: 'name' },
        { text: 'Price', value: 'price' },
        { text: 'Venue', value: 'venue' },
        { text: 'Valid From', value: 'valid_from' },
        { text: 'Valid To', value: 'valid_to' },
        { text: 'Subscribers', value: 'activeSubscriberCount' },
        { text: 'Status', value: 'active' },
        { text: 'Actions', value: 'actions', sortable: false },
      ],
      noDataText: 'No subscription plans available',
      organizationId: null,
      snackbar: null,
    }
  },
  computed: {
    capacityListHeaders() {
      const headers = this.organizationHeaders(this.headers).slice()
      headers.splice(1, 0, { text: this.adminText('organizationAll.ga_capacity', 'Subscription capacity'), value: 'generalAdmissionCapacity.capacityLimit', sortable: false })

      return headers
    },
    isSubscriptionDataLocked() {
      return this.purchaseCountLoading || this.purchaseCount > 0 || this.hasPurchaseHistory
    },
    seasonOptions() {
      const seasons = new Set()
      this.subscriptionPlans.forEach(plan => {
        const season = this.getSeasonLabel(plan)
        if (season) {
          seasons.add(season)
        }
      })

      return [
        { text: 'All seasons', value: null },
        ...Array.from(seasons)
          .sort()
          .reverse()
          .map(season => ({ text: season, value: season })),
      ]
    },
    statusOptions() {
      return [
        { text: 'All statuses', value: null },
        { text: 'Active', value: 'active' },
        { text: 'Inactive', value: 'inactive' },
      ]
    },
    filteredSubscriptionPlans() {
      const term = (this.search || '').trim().toLowerCase()

      return this.subscriptionPlans.filter(plan => {
        if (this.seasonFilter && this.getSeasonLabel(plan) !== this.seasonFilter) {
          return false
        }
        if (this.statusFilter === 'active' && !plan.active) {
          return false
        }
        if (this.statusFilter === 'inactive' && plan.active) {
          return false
        }
        if (term) {
          const haystack = [plan.name, plan.venue ? plan.venue.name : ''].join(' ').toLowerCase()
          if (!haystack.includes(term)) {
            return false
          }
        }

        return true
      }).map(plan => ({
        ...plan,
        activeSubscriberCount: (plan.subscriptionPurchases || []).filter(purchase => purchase.active).length,
      }))
    },
  },
  watch: {
    selectedVenue() {
      // clear field-level error when user changes selection
      this.venueErrorMessage = ''
    },
  },
  async created() {
    this.fetchSubscriptionPlans()
    this.fetchEventTemplates()
    this.fetchVenues()
  },
  methods: {
    venueHasSeatMap(venue) {
      return !!(venue && venue.seats && venue.seats.length && venue.predefinedSections)
    },
    getAdmissionLabel(venue) {
      return this.venueHasSeatMap(venue) ? this.adminText('organizationAll.numerirano_382e78f8', 'Numerirano') : this.adminText('organizationAll.nenumerirano_f62a23af', 'Nenumerirano')
    },
    formatVenueName(venue) {
      if (!venue) return ''

      return `${venue.name} - ${this.getAdmissionLabel(venue)}`
    },
    normalizeVenue(venue) {
      return {
        ...venue,
        id: Number(venue.id),
        displayName: this.formatVenueName(venue),
      }
    },
    async fetchVenues() {
      try {
        const response = await this.$apollo.query({
          query: gql`
            query GetOrganizationVenuesForSelect($organizationSlug: String!) {
              getOrganizationVenues(organizationSlug: $organizationSlug) {
                organizationVenues {
                  venue {
                    id
                    name
                    seats
                    predefinedSections
                  }
                }
              }
            }
          `,
          variables: { organizationSlug: this.organizationSlug },
        })
        const data = response && response.data ? response.data : {}
        const orgVenues =
          data.getOrganizationVenues && data.getOrganizationVenues.organizationVenues
            ? data.getOrganizationVenues.organizationVenues
            : []
        this.venues = orgVenues.map(ov => this.normalizeVenue(ov.venue))
      } catch (error) {
        console.error('Error fetching venues:', error)
        this.venues = []
      }
    },
    async fetchEventTemplates() {
      try {
        const response = await this.$apollo.query({
          query: gql`
            query getOrganizationEventTemplates($organizationSlug: String!) {
              getOrganizationEventTemplates(organizationSlug: $organizationSlug) {
                id
                title
              }
            }
          `,
          variables: {
            organizationSlug: this.organizationSlug,
          },
        })

        if (response.data && response.data.getOrganizationEventTemplates) {
          this.eventTemplates = response.data.getOrganizationEventTemplates
        }
      } catch (error) {
        console.error('Error fetching event templates:', error)
        this.snackbar = {
          open: true,
          messageProp: 'Error fetching event templates',
          color: 'error',
        }
      }
    },
    navigateToCreateSubscription() {
      this.$router.push({
        name: theaterOrganizationRouteNames.theaterOrganizationTheaterSubscriptionsPlanCreate,
        params: { organizationSlug: this.organizationSlug },
      })
    },
    previewSubscriptionsPurchasePage() {
      //
      this.$router.push({
        name: sharedRouteNames.theaterSubscriptionsPurchase,
        params: { organizationSlug: this.organizationSlug },
      })
    },
    async fetchSubscriptionPlans() {
      this.loading = true
      try {
        const response = await this.$apollo.query({
          query: getTheaterSubscriptions,
          variables: {
            organizationSlug: this.organizationSlug,
          },
          fetchPolicy: 'network-only',
        })
        console.log('Subscription plans response:', response)

        if (response.data && response.data.getTheaterSubscriptions) {
          this.subscriptionPlans = [...response.data.getTheaterSubscriptions]
          console.log('Subscription plans set:', this.subscriptionPlans)
        } else {
          console.error('No subscription plans data in response')
          this.subscriptionPlans = []
        }
      } catch (error) {
        console.error('Error fetching subscription plans:', error)
        this.snackbar = {
          open: true,
          messageProp: 'Error fetching subscription plans',
          color: 'error',
        }
        this.subscriptionPlans = []
      } finally {
        this.loading = false
      }
    },
    editSubscriptionPlan(item) {
      this.isEditing = true
      this.subscriptionPlan = {
        ...item,
        valid_from: new Date(item.valid_from).toISOString().substr(0, 10),
        valid_to: new Date(item.valid_to).toISOString().substr(0, 10),
        event_template_id: item.event_template_id || null,
        schedule_sales_availability: item.schedule_sales_availability || false,
        available_from: item.available_from
          ? new Date(item.available_from).toISOString().substr(0, 10)
          : '',
        available_to: item.available_to
          ? new Date(item.available_to).toISOString().substr(0, 10)
          : '',
        enable_pre_order: item.enable_pre_order || false,
        pre_order_days: item.pre_order_days || 14,
        description: item.description || '',
        venueId: item.venueId || null,
      }
      this.selectedVenue =
        this.subscriptionPlan.venueId != null ? Number(this.subscriptionPlan.venueId) : null
      this.planCoverUrl = item.coverUrl || null
      this.originalPlanPrice = Number(item.price)
      this.hasPurchaseHistory = !!item.hasPurchaseHistory
      this.venueErrorMessage = ''
      // Determine if there are any purchases for this plan to lock venue selection
      this.purchaseCount = 0
      this.purchaseCountLoading = true
      this.$apollo
        .query({
          query: getTheaterSubscription,
          variables: { id: parseInt(item.id) },
          fetchPolicy: 'network-only',
        })
        .then(({ data }) => {
          const details = data && data.getTheaterSubscription
          this.purchaseCount =
            details && details.subscriptionPurchases ? details.subscriptionPurchases.length : 0
          this.hasPurchaseHistory = !!(details && details.hasPurchaseHistory)
        })
        .catch(() => {
          this.purchaseCount = 0
        })
        .finally(() => {
          this.purchaseCountLoading = false
        })
      this.showSubscriptionPlanModal = true
    },
    deleteSubscriptionPlan(item) {
      this.subscriptionPlanToDelete = item
      this.showDeleteDialog = true
    },
    async confirmDelete() {
      try {
        const { errors } = await this.$apollo.mutate({
          mutation: deleteTheaterSubscription,
          variables: {
            id: parseInt(this.subscriptionPlanToDelete.id),
            organizationSlug: this.organizationSlug,
          },
        })

        if (errors) {
          this.snackbar = {
            open: true,
            messageProp: errors[0] && errors[0].message ? errors[0].message : this.organizationMessage('organizationAll.error_deleting_subscription_plan_326420a0', 'Error deleting subscription plan'),
            color: 'error',
          }
          this.showDeleteDialog = false

          return
        }
        this.subscriptionPlans = this.subscriptionPlans.filter(
          plan => plan.id !== this.subscriptionPlanToDelete.id,
        )

        this.showDeleteDialog = false
        this.snackbar = {
          open: true,
          messageProp: 'Subscription plan deleted successfully',
          color: 'success',
        }
      } catch (error) {
        const message =
          (error &&
            error.graphQLErrors &&
            error.graphQLErrors[0] &&
            error.graphQLErrors[0].message) ||
          error.message ||
          'Error deleting subscription plan'
        this.snackbar = {
          open: true,
          messageProp: message,
          color: 'error',
        }
        this.showDeleteDialog = false
      }
    },
    async saveSubscriptionPlan(confirmedEmpty = false) {
      if (this.formLoading) return
      if (this.$refs.form.validate()) {
        this.formLoading = true
        try {
          if (!(await this.prepareCapacitySave(confirmedEmpty))) return
          if (
            this.isSubscriptionDataLocked &&
            Number(this.subscriptionPlan.price) !== Number(this.originalPlanPrice)
          ) {
            this.snackbar = {
              open: true,
              messageProp: 'Price cannot be changed after purchases exist for this plan.',
              color: 'error',
            }

            return
          }
          // FE guard: prevent venue change when purchases exist
          if (
            this.isSubscriptionDataLocked &&
            Number(this.selectedVenue) !== Number(this.subscriptionPlan.venueId)
          ) {
            this.snackbar = {
              open: true,
              messageProp: 'Venue cannot be changed after purchases exist for this plan.',
              color: 'error',
            }
            // revert selection to original
            this.selectedVenue = Number(this.subscriptionPlan.venueId)
            this.venueErrorMessage = 'Venue cannot be changed after purchases exist for this plan.'

            return
          }
          const variables = {
            input: {
              id: parseInt(this.subscriptionPlan.id),
              organizationSlug: this.organizationSlug,
              name: this.subscriptionPlan.name,
              price: parseFloat(this.subscriptionPlan.price),
              valid_from: new Date(this.subscriptionPlan.valid_from),
              valid_to: new Date(this.subscriptionPlan.valid_to),
              active: this.subscriptionPlan.active,
              venueId: parseInt(this.selectedVenue),
              description: this.subscriptionPlan.description || undefined,
              coverUrl: this.planCoverUrl || null,
              schedule_sales_availability: Boolean(
                this.subscriptionPlan.schedule_sales_availability,
              ),
              enable_pre_order: this.subscriptionPlan.schedule_sales_availability
                ? Boolean(this.subscriptionPlan.enable_pre_order)
                : false,
              pre_order_days:
                this.subscriptionPlan.schedule_sales_availability &&
                this.subscriptionPlan.enable_pre_order
                  ? parseInt(this.subscriptionPlan.pre_order_days)
                  : null,
            },
          }

          // Add scheduling fields if enabled
          if (this.subscriptionPlan.schedule_sales_availability) {
            variables.input.available_from = new Date(this.subscriptionPlan.available_from)
            variables.input.available_to = new Date(this.subscriptionPlan.available_to)
          }

          // Update existing plan
          const response = await this.$apollo.mutate({
            mutation: updateTheaterSubscription,
            variables,
          })

          const updatedPlan =
            response && response.data ? response.data.updateTheaterSubscription : null
          if (!updatedPlan) {
            const serverMessage =
              (response && response.errors && response.errors[0] && response.errors[0].message) ||
              'Error updating subscription plan'
            if (/Venue cannot be changed/i.test(serverMessage)) {
              this.venueErrorMessage = serverMessage
              this.selectedVenue = Number(this.subscriptionPlan.venueId)
              this.hasPurchaseHistory = true
            }
            if (/Price cannot be changed/i.test(serverMessage)) {
              this.subscriptionPlan.price = this.originalPlanPrice
              this.hasPurchaseHistory = true
            }
            this.snackbar = {
              open: true,
              messageProp: serverMessage,
              color: 'error',
            }

            return
          }

          const index = this.subscriptionPlans.findIndex(plan => plan.id === updatedPlan.id)
          if (index !== -1) {
            this.subscriptionPlans.splice(index, 1, updatedPlan)
          }

          // Cover is saved directly on TheaterSubscription.coverUrl now
          await this.fetchSubscriptionPlans()

          this.showSubscriptionPlanModal = false
          this.snackbar = {
            open: true,
            messageProp: 'Subscription plan updated successfully',
            color: 'success',
          }
        } catch (error) {
          console.error('Error updating subscription plan:', error)
          const msg =
            (error &&
              error.graphQLErrors &&
              error.graphQLErrors[0] &&
              error.graphQLErrors[0].message) ||
            error.message ||
            'Error updating subscription plan'
          // If BE guard blocked venue change, reflect it on the field
          if (/Venue cannot be changed/i.test(msg)) {
            this.venueErrorMessage = msg
            this.selectedVenue = Number(this.subscriptionPlan.venueId)
            this.hasPurchaseHistory = true
          }
          if (/Price cannot be changed/i.test(msg)) {
            this.subscriptionPlan.price = this.originalPlanPrice
            this.hasPurchaseHistory = true
          }
          this.snackbar = {
            open: true,
            messageProp: msg,
            color: 'error',
          }
        } finally {
          this.formLoading = false
        }
      }
    },
    formatSubscriptionType(type) {
      const typeMap = {
        yearly: 'Yearly',
        yearly_discounted: 'Yearly Discounted',
        half_year_1: 'Half Year 1',
        half_year_1_discounted: 'Half Year 1 Discounted',
        half_year_2: 'Half Year 2',
        half_year_2_discounted: 'Half Year 2 Discounted',
      }

      return typeMap[type] || type
    },
    formatDate(date) {
      if (!date) return ''

      const parsed = new Date(date)
      if (Number.isNaN(parsed.getTime())) return ''

      return `${parsed.getDate()}/${parsed.getMonth() + 1}/${parsed.getFullYear()}`
    },
    // There is no season column on theater_subscription, so the season is derived from the
    // plan's validity range: a range crossing a year boundary reads as "2025/2026".
    getSeasonLabel(plan) {
      if (!plan || !plan.valid_from) return ''
      const fromYear = new Date(plan.valid_from).getFullYear()
      if (Number.isNaN(fromYear)) return ''
      const toYear = plan.valid_to ? new Date(plan.valid_to).getFullYear() : fromYear
      if (Number.isNaN(toYear) || toYear === fromYear) {
        return `${fromYear}`
      }

      return `${fromYear}/${toYear}`
    },
  },
}
</script>

<style scoped>
.card {
  margin-bottom: 20px;
}
.subscription-plan-edit-modal { padding: 0; font-family: 'Source Sans Pro', sans-serif; }
.clickable-row {
  cursor: pointer;
}
.ts-plans-container {
  padding: 0 !important;
  margin: 0 !important; /* left-aligned to match membership */
  display: flex;
  flex-direction: column;
}
.subs-title {
  font-family: 'Raleway';
  font-style: normal;
  font-weight: 600;
  font-size: 20px;
  line-height: 120%;
  letter-spacing: 0;
  font-feature-settings: 'pnum' on, 'lnum' on;
  color: #ff4c00;
  margin-bottom: 0.5rem;
}
.page-description {
  font-family: 'Source Sans Pro';
  font-style: normal;
  font-weight: 400;
  font-size: 13px;
  color: #979797;
  margin-bottom: 1rem;
}
.add-symbol {
  font-size: 24px;
  margin-right: 8px;
}

/* Keep overrides local to the plan list, including Vuetify's progress row. */
.card { background: transparent; box-shadow: none !important; border: 0; }
.card >>> .v-card__text { padding: 0 !important; }
.plans-toolbar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  margin: 24px 0 12px;
}
.plan-filters { display: flex; gap: 12px; flex: 0 0 256px; }
.plan-filters > * { width: 122px; min-width: 122px; margin: 0; padding: 0; }
.plan-filters >>> .v-input__slot {
  min-height: 44px !important;
  height: 44px;
  padding: 0 10px !important;
  border: 1px solid #dedede !important;
  border-radius: 3px;
  box-shadow: none !important;
}
.plan-filters >>> .v-input--is-focused .v-input__slot { border-color: #007cba !important; }
.plan-filters >>> .v-label { font-size: 12px; top: 12px; }
.plan-filters >>> .v-label--active {
  top: 0;
  transform: translateY(-50%) scale(.85);
  background: white;
  padding: 0 3px;
}
.plan-filters >>> .v-select__selections { padding: 0 !important; flex-wrap: nowrap; min-width: 0; }
.plan-filters >>> .v-select__selection {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 13px;
  margin: 0;
  max-width: calc(100% - 4px);
}
.plan-filters >>> .v-select__selections input { width: 0; min-width: 0; padding: 0; }
.plan-filters >>> .v-input__append-inner { margin-top: 10px !important; padding-left: 4px; }
.plan-filters >>> .v-input__icon .v-icon { font-size: 18px; }
.plan-actions { display: flex; flex-wrap: wrap; gap: 12px; }
.plan-actions >>> .main-button {
  font-size: 12px;
  height: 36px;
  margin: 0;
  padding: 0 16px;
  box-shadow: none !important;
}
.plan-search { max-width: 320px; margin: 0 0 20px; padding-top: 0; }
.plan-search >>> input, .plan-search >>> .v-label { font-size: 14px; }
.compact-table { border: 1px solid #eee; border-radius: 4px; box-shadow: none; }
.compact-table { font-family: 'Source Sans Pro', sans-serif; }
.compact-table >>> .v-table__overflow > table { width: 100%; height: auto; min-width: 1050px; table-layout: fixed; }
.compact-table >>> .v-table > thead tr:not(.v-datatable__progress) th {
  padding: 0 8px !important;
  height: 36px;
  font-size: 12px;
  white-space: normal;
  word-break: normal;
  overflow-wrap: normal;
  background: #f5f5f5;
  border-bottom: 0 !important;
}
.compact-table >>> .v-table > thead tr { height: 36px !important; }
.compact-table >>> .v-table > thead tr.v-datatable__progress { height: 0 !important; }
.compact-table >>> .v-datatable__progress th {
  padding: 0 !important;
  height: 0;
  border: 0 !important;
}
.compact-table >>> tbody > .clickable-row > td {
  padding: 16px 8px !important;
  height: 64px;
  font-size: 12px;
  line-height: 1.6;
  border-bottom: 1px solid #f5f5f5;
  overflow-wrap: break-word;
}
.compact-table >>> .v-table > thead th:nth-child(1) { width: 18%; }
.compact-table >>> .v-table > thead th:nth-child(2) { width: 10%; }
.compact-table >>> .v-table > thead th:nth-child(3) { width: 7%; }
.compact-table >>> .v-table > thead th:nth-child(4) { width: 13%; }
.compact-table >>> .v-table > thead th:nth-child(5), .compact-table >>> .v-table > thead th:nth-child(6) { width: 12%; }
.compact-table >>> .v-table > thead th:nth-child(7) { width: 8%; }
.compact-table >>> .v-table > thead th:nth-child(8) { width: 9%; }
.compact-table >>> .v-table > thead th:nth-child(9) { width: 11%; }
.compact-table >>> .clickable-row > td:nth-child(3), .compact-table >>> .clickable-row > td:nth-child(5),
.compact-table >>> .clickable-row > td:nth-child(6) { white-space: nowrap; }
.compact-table >>> .v-chip { margin: 0; height: 24px; font-size: 12px; }
.compact-table >>> .v-chip__content { padding: 0 8px; height: 24px; }
.compact-table >>> .v-datatable__actions { height: auto; min-height: 48px; font-size: 12px; }
.compact-table >>> .v-datatable__actions__select { width: auto; }
.compact-table >>> .v-datatable__actions__pagination { flex-shrink: 0; white-space: nowrap; }
.plan-row-actions { white-space: nowrap; }
.plan-row-actions .v-btn { margin: 0; width: 36px; height: 36px; }
.plan-name-button { text-align: left; font: inherit; color: inherit; overflow-wrap: anywhere; }
.plan-name-button:focus-visible { outline: 2px solid #0277bd; outline-offset: 3px; }
.plan-mobile-sort { display: flex; align-items: center; gap: 12px; margin: 4px 0 16px; }
.plan-mobile-sort > .v-input { min-width: 0; margin: 0; padding: 0; }
.plan-mobile-sort > .v-btn { width: 44px; height: 44px; flex: 0 0 44px; margin: 0; }
.compact-table >>> .v-datatable__expand-col { padding: 0 !important; height: auto !important; }
@media (max-width: 959px) {
  .compact-table { border: 0; border-radius: 0; }
  .compact-table >>> .v-table > thead { display: none; }
  .compact-table >>> .v-table__overflow > table, .compact-table >>> .v-table > tbody { display: block; min-width: 0; width: 100%; }
  .compact-table >>> .v-datatable__expand-row, .compact-table >>> .v-datatable__expand-col { display: block; width: 100%; }
  .compact-table >>> tbody > .clickable-row { display: block; padding: 16px 0; border-top: 1px solid #d9e1e6; background: white; }
  .compact-table >>> tbody > .clickable-row:hover { background: white !important; }
  .compact-table >>> tbody > .clickable-row > td {
    display: flex; align-items: center; justify-content: space-between; gap: 16px;
    width: 100%; height: auto; min-height: 40px; padding: 6px 4px !important;
    border: 0; font-size: 14px; line-height: 1.4; white-space: normal; text-align: right;
  }
  .compact-table >>> tbody > .clickable-row > td::before { content: attr(data-label); flex: 0 0 45%; color: #52616b; text-align: left; }
  .compact-table >>> tbody > .clickable-row > .plan-name { padding-bottom: 12px !important; text-align: left; }
  .compact-table >>> tbody > .clickable-row > .plan-name::before { display: none; }
  .plan-name-button { width: 100%; min-height: 44px; font-size: 17px; font-weight: 600; color: #007cba; }
  .compact-table >>> tbody > .clickable-row > .plan-row-actions { justify-content: flex-end; gap: 8px; }
  .plan-row-actions::before { margin-right: auto; }
  .plan-row-actions .v-btn { width: 44px; height: 44px; }
  .compact-table >>> .v-table__overflow { overflow: visible; }
  .compact-table >>> .v-datatable__actions { border-top: 1px solid #d9e1e6; }
  .capacity-expand { min-height: 44px; }
}
@media (max-width: 600px) {
  .plan-filters, .plan-search { flex-basis: 100%; max-width: none; }
  .plan-filters > * { flex: 1; }
  .plan-actions { width: 100%; }
  .plan-actions >>> .main-button { flex: 1 1 220px; }
  .compact-table { font-family: 'Source Sans Pro', sans-serif; }

  .compact-table >>> .v-datatable__actions {
    padding: 8px;
    flex-wrap: wrap !important;
    justify-content: flex-end;
    gap: 4px;
  }
  .compact-table >>> .v-datatable__actions__select {
    flex: 1 1 100%;
    margin: 0;
    justify-content: flex-end;
    white-space: nowrap;
  }
  .compact-table >>> .v-datatable__actions__select .v-select {
    flex: 0 0 56px;
    margin: 0 8px;
  }
  .compact-table >>> .v-datatable__actions__pagination { margin: 0 8px; }
  .compact-table >>> .v-datatable__actions .v-btn { margin: 0 4px; }
}
</style>

<style scoped>
.subscription-plan-edit-modal { max-height: calc(100vh - 48px); max-height: calc(100dvh - 48px); display: flex; flex-direction: column; }
.subscription-plan-edit-modal > .v-card__title { flex-wrap: nowrap; gap: 16px; padding: 24px 28px; border-bottom: 1px solid #e8edf0; }
.subscription-plan-edit-modal > .v-card__title .headline { flex: 1; min-width: 0; font-family: 'Raleway', sans-serif !important; font-size: 22px !important; font-weight: 600; color: #26343e; }
.subscription-plan-edit-modal > .v-card__title, .subscription-plan-edit-modal > .v-card__actions { flex: 0 0 auto; }
.subscription-plan-edit-modal > .v-card__text { flex: 1 1 auto; overscroll-behavior: contain; overflow-y: auto; min-height: 0; padding: 24px 28px !important; }
.subscription-plan-edit-modal > .v-card__actions { padding: 16px 28px; gap: 12px; border-top: 1px solid #e8edf0; background: white; }
.subscription-plan-edit-modal > .v-card__actions >>> .main-button { margin: 0; font-size: 14px; min-width: 112px; height: 40px; }
.subscription-plan-form--with-capacity { display: grid; grid-template-columns: minmax(0, 1fr) minmax(340px, .9fr); gap: 32px; align-items: start; }
.plan-edit-details { min-width: 0; }
.plan-capacity-panel { padding-left: 28px; border-left: 1px solid #e8edf0; }
.plan-capacity-panel >>> .capacity-summary { flex-direction: column; align-items: stretch; gap: 10px; }
.plan-capacity-panel >>> .capacity-value { flex: none; }
.plan-list-capacity { padding: 24px; max-width: 800px; }
.capacity-expand { margin: 0; min-width: 64px; border: 1px solid #d9e1e6; font-variant-numeric: tabular-nums; }
.capacity-expand:focus-visible { outline: 2px solid #0277bd; outline-offset: 3px; }
.capacity-expand .v-icon { margin-left: 8px; color: #52616b; }
@media (max-width: 959px) {
  .subscription-plan-form--with-capacity { grid-template-columns: minmax(0, 1fr); gap: 24px; }
  .plan-capacity-panel { padding: 24px 0 0; border-left: 0; border-top: 1px solid #e8edf0; }
}
@media (max-width: 599px) {
  .subscription-plan-edit-modal { max-height: 100vh; max-height: 100dvh; height: 100vh; height: 100dvh; }
  .subscription-plan-edit-modal > .v-card__title { padding: 20px; }
  .subscription-plan-edit-modal > .v-card__title .headline { font-size: 20px !important; }
  .subscription-plan-edit-modal > .v-card__text { padding: 20px !important; }
  .subscription-plan-edit-modal > .v-card__actions { padding: 12px 20px max(12px, env(safe-area-inset-bottom)); }
  .subscription-plan-edit-modal > .v-card__actions >>> .main-button { flex: 1; min-height: 44px; }
  .subscription-plan-edit-modal .plan-edit-fields { flex-direction: column; align-items: stretch !important; }
  .subscription-plan-edit-modal .plan-edit-fields > .v-input { width: 100%; place-items: start !important; }
  .subscription-plan-edit-modal .layout { flex-wrap: wrap; }
  .subscription-plan-edit-modal .layout > .flex { flex-basis: 100%; padding-left: 0 !important; padding-right: 0 !important; }
  .plan-list-capacity { padding: 16px; }
  .capacity-expand { min-height: 44px; }
}
</style>

<!-- Vuetify detaches the dialog wrapper into v-app; target only this named dialog. -->
<style>
.subscription-plan-dialog .plan-edit-close { flex: 0 0 44px; width: 44px; height: 44px; margin: 0; }
@media (max-width: 599px) {
  .subscription-plan-dialog.v-dialog--fullscreen { transition-property: transform, opacity; height: 100vh; height: 100dvh; max-height: 100vh; max-height: 100dvh; overflow: hidden; }
  .subscription-plan-dialog.v-dialog--fullscreen > .subscription-plan-edit-modal { transition-property: box-shadow, transform, opacity; min-height: 0; height: 100%; max-height: 100%; }
}
</style>
